/**
 * A new competition series document — the ONE construction the admin panel
 * (`useSeries.createSeries`, fed by the series editor) and the MCP server
 * (`create_series`) both run.
 *
 * A series has no draft state: the app has always created it `active`, and
 * so does this builder. The MCP tool creates it without rounds
 * (`competitionIds: []`, `scoring.groupMapping: {}` — the defaults below); the
 * editor may already carry rounds and a group mapping on its first save, and
 * those pass through unchanged.
 *
 * Pure: the caller owns the document id (`series_<Date.now()>` on the
 * client), `createdAt` / `updatedAt` (SDK sentinels), and on MCP writes the
 * actor stamp (`lastModifiedBy`, `lastModifiedVia`, `lastModifiedByClient`).
 * The client's series writes carry no actor stamp; there is no series audit
 * trigger.
 */
import { gymDefaultLang, localizedFromInput } from "./localizedText.js";
import { buildOrganizerContact } from "./organizerContact.js";

/**
 * The default points table of a placement series — `{ place: points }` for
 * places 1–10. The series editor pre-fills its table with it, and the MCP
 * `create_series` tool uses it when the caller gives no `placementPoints`.
 * Places past the table get `scoring.defaultPoints` (the editor's default: 1).
 */
export const DEFAULT_SERIES_POINT_MAP = Object.freeze({ "1": 25, "2": 20, "3": 16, "4": 13, "5": 11, "6": 10, "7": 9, "8": 8, "9": 7, "10": 6 });

/**
 * Build a series document from the editor-shaped input:
 * `{ name, description, coverImageUrl, competitionIds, scoring: { type,
 * pointMap?, defaultPoints?, countBest, groupMapping } }`.
 *
 * Only those fields are taken; `status` is always `"active"`. A missing scoring
 * type is the editor's default, `raw_points`. `pointMap` and
 * `defaultPoints` appear only when the input has them (the editor sends them
 * for a placement series only), so a raw-points series gets no such keys.
 *
 * `description` is stored per language (`descriptionI18n`) plus its mirror; a
 * `descriptionI18n` input is the whole text, and without one a plain
 * `description` means the default language of the gym `gymCtx`
 * (`{ gymId, country }`) — see `localizedFromInput` in `localizedText.js`.
 */
export function buildSeriesDoc(input, gymCtx) {
  const data = input || {};
  const scoringIn = data.scoring || {};
  const scoring = {
    type: scoringIn.type || "raw_points",
    ...(scoringIn.pointMap !== undefined ? { pointMap: scoringIn.pointMap } : {}),
    ...(scoringIn.defaultPoints !== undefined ? { defaultPoints: scoringIn.defaultPoints } : {}),
    countBest: scoringIn.countBest ?? null,
    groupMapping: scoringIn.groupMapping || {},
  };
  return {
    name: data.name ?? "",
    ...localizedFromInput(data, "description", gymDefaultLang(gymCtx)),
    coverImageUrl: data.coverImageUrl ?? null,
    // Who to call; `null` falls back to the gym's contact on every surface.
    organizerContact: buildOrganizerContact(data.organizerContact),
    competitionIds: Array.isArray(data.competitionIds) ? data.competitionIds : [],
    scoring,
    status: "active",
  };
}
